/* import __COLOCATED_TEMPLATE__ from './fin-identity-editor.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { ref } from 'ember-ref-bucket';
import { defer } from 'rsvp';
//@ts-expect-error
import DefaultAvatars from '@intercom/pulse/lib/default-avatars';
import type FinSuffixSettings from 'embercom/models/fin-suffix-settings';

export interface FinIdentityEditorArgs {
  identity: $TSFixMe; // app/models/operator-identity
  identityPreviewImgSrc: string;
  identityPreviewDarkImgSrc: string;
  onAvatarImgChange: (imgSrc: string | unknown) => void;
  onAvatarDarkImgChange: (imgSrc: string | unknown) => void;
  finSuffixSettings?: FinSuffixSettings;
}

export default class FinIdentityEditor extends Component<FinIdentityEditorArgs> {
  @service declare intercomEventService: $TSFixMe;
  @service declare store: $TSFixMe;
  @service declare appService: AppService;

  @ref('inputElement') inputElement!: HTMLInputElement;
  @ref('darkInputElement') darkInputElement!: HTMLInputElement;
  @tracked fileWasChosen = false;
  @tracked darkFileWasChosen = false;
  @tracked finSuffixSettings: $TSFixMe = null;

  constructor(owner: unknown, args: FinIdentityEditorArgs) {
    super(owner, args);
    this.loadFinSuffixSettings();
  }

  async loadFinSuffixSettings() {
    try {
      this.finSuffixSettings = await this.store.findRecord(
        'fin-suffix-settings',
        this.appService.app.id,
      );
    } catch (error) {
      console.error('Failed to load fin suffix settings:', error);
    }
  }

  get isLoading() {
    return !this.args.identity;
  }

  get savedAvatarIsCustom() {
    return this.args.identity.avatarType === 'custom';
  }

  get savedDarkAvatarIsCustom() {
    return this.args.identity.avatarDarkType === 'custom';
  }

  get identityPreview() {
    return {
      avatarType: this.args.identity.avatarType,
      avatarData: {
        avatarShape: this.args.identity.avatarData.avatarShape,
        url: this.args.identityPreviewImgSrc,
      },
    };
  }

  get identityPreviewDark() {
    return {
      avatarType: this.args.identity.avatarDarkType,
      avatarData: {
        avatarShape: this.args.identity.avatarData.avatarShape,
        url: this.args.identityPreviewDarkImgSrc,
      },
    };
  }

  @action
  restoreAvatarToDefault() {
    this.args.identity.avatarType = 'default';
    this.args.identity.set('avatarUpload', null);
    this.args.onAvatarImgChange(DefaultAvatars.fin[128]);
  }

  @action
  restoreAvatarToDefaultDark() {
    this.args.identity.avatarDarkType = 'default';
    this.args.identity.set('avatarDarkUpload', null);
    this.args.onAvatarDarkImgChange(DefaultAvatars.fin['128-dark']);
  }

  @action
  openFilePicker() {
    this.inputElement.click();
  }

  @action
  openFilePickerDark() {
    this.darkInputElement.click();
  }

  @action
  onAvatarHovered() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'hovered',
      object: 'bot_identity_avatar',
      place: 'overview_personality',
      section: 'ai_agent',
      hasCustomAvatar: this.savedAvatarIsCustom,
    });
  }

  @action
  onAvatarDarkHovered() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'hovered',
      object: 'bot_identity_avatar_dark',
      place: 'overview_personality',
      section: 'ai_agent',
      hasCustomAvatar: this.savedAvatarIsCustom,
    });
  }

  @action
  async setCustomAvatarFromFilePicker(fileList: FileList) {
    if (fileList.length < 1) {
      this.fileWasChosen = false;
      return;
    } else {
      this.fileWasChosen = true;
      let dataUrl = await this._readFileAsDataURL(fileList[0]);
      this.args.identity.avatarType = 'custom';
      this.args.identity.set('avatarUpload', fileList[0]);

      this.args.onAvatarImgChange(dataUrl);

      this._clearFilePicker();
    }
  }

  @action
  async setCustomAvatarDarkFromFilePicker(fileList: FileList) {
    if (fileList.length < 1) {
      this.darkFileWasChosen = false;
      return;
    } else {
      this.darkFileWasChosen = true;
      let dataUrl = await this._readFileAsDataURL(fileList[0]);
      this.args.identity.avatarDarkType = 'custom';
      this.args.identity.set('avatarDarkUpload', fileList[0]);

      this.args.onAvatarDarkImgChange(dataUrl);

      this._clearFilePickerDark();
    }
  }

  _readFileAsDataURL(file: File): Promise<unknown> {
    let deferred = defer();
    let reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && typeof e.target.result === 'string') {
        deferred.resolve(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    return deferred.promise;
  }

  _clearFilePicker() {
    if (this.inputElement) {
      this.inputElement.value = '';
    }
  }

  _clearFilePickerDark() {
    if (this.darkInputElement) {
      this.darkInputElement.value = '';
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::FinIdentityEditor': typeof FinIdentityEditor;
  }
}
